import { Link, useLocation, useNavigate } from "react-router-dom";
import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useJourney } from "@/context/JourneyContext";
import { BrandLogo } from "@/components/brand";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  LayoutDashboard, BookOpen, User, LogOut, Menu, X, Lock,
  ClipboardList, Lightbulb, ListChecks, Trophy, BarChart3, Rocket, ShieldCheck,
} from "lucide-react";
import { toast } from "sonner";

// Desktop: concise. Mobile: full journey list (spec §9).
const DESKTOP_NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { to: "/panduan", label: "Panduan", icon: BookOpen },
  { to: "/profil", label: "Akun", icon: User },
];

// Journey nav entries mapped to step index for lock state.
const JOURNEY_NAV = [
  { to: "/assessment", label: "Profil Bisnis", icon: ClipboardList, step: 0 },
  { to: "/ideas", label: "Ide Bisnis", icon: Lightbulb, step: 1 },
  { to: "/validation", label: "Validasi", icon: ListChecks, step: 3 },
  { to: "/comparison", label: "Business Winner", icon: Trophy, step: 4 },
  { to: "/blueprint", label: "Blueprint", icon: BarChart3, step: 5 },
  { to: "/launch", label: "Rencana 7 Hari", icon: Rocket, step: 6 },
];

function initials(name = "") {
  return name.split(" ").map((n) => n[0]).slice(0, 2).join("").toUpperCase() || "U";
}

function LayoutInner({ children }) {
  const { user, logout } = useAuth();
  const { states } = useJourney();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const isAdmin = user?.role === "admin";

  const handleLogout = async () => {
    await logout();
    toast.success("Anda telah keluar.");
    navigate("/login");
  };

  // Profil Bisnis (step 0) is always reachable; others locked by prerequisites.
  const isLocked = (step) => step !== 0 && states[step] === "locked";

  return (
    <div className="min-h-screen bg-surface">
      <header className="sticky top-0 z-40 border-b border-borderc bg-white/90 backdrop-blur-md" data-testid="dashboard-header">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex h-16 items-center justify-between">
            <BrandLogo />

            <nav className="hidden md:flex items-center gap-1">
              {DESKTOP_NAV.map(({ to, label, icon: Icon }) => {
                const active = location.pathname === to;
                return (
                  <Link key={to} to={to} data-testid={`nav-${label.toLowerCase()}`}
                    className={`flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                      active ? "bg-brand/10 text-brand" : "text-ink-soft hover:bg-slate-100 hover:text-ink"}`}>
                    <Icon className="h-4 w-4" />{label}
                  </Link>
                );
              })}
              {isAdmin && (
                <Link to="/admin" data-testid="nav-admin"
                  className={`flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                    location.pathname === "/admin" ? "bg-brand/10 text-brand" : "text-ink-soft hover:bg-slate-100 hover:text-ink"}`}>
                  <ShieldCheck className="h-4 w-4" />Admin
                </Link>
              )}
            </nav>

            <div className="flex items-center gap-2">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button data-testid="user-menu-trigger"
                    className="flex items-center gap-2 rounded-full border border-borderc bg-white py-1.5 pl-1.5 pr-3 transition-colors hover:bg-slate-50">
                    <span className="flex h-7 w-7 items-center justify-center rounded-full bg-brand text-xs font-semibold text-white">
                      {initials(user?.name)}
                    </span>
                    <span className="hidden sm:block max-w-[120px] truncate text-sm font-medium text-ink">{user?.name}</span>
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56 bg-white">
                  <DropdownMenuLabel className="truncate">
                    <div className="font-semibold text-ink">{user?.name}</div>
                    <div className="text-xs font-normal text-ink-muted truncate">{user?.email}</div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate("/profil")} data-testid="menu-profil">
                    <User className="mr-2 h-4 w-4" /> Akun
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={handleLogout} data-testid="logout-button" className="text-red-600 focus:text-red-600">
                    <LogOut className="mr-2 h-4 w-4" /> Keluar
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>

              <button className="md:hidden rounded-lg p-2 text-ink hover:bg-slate-100"
                onClick={() => setMobileOpen((v) => !v)} data-testid="mobile-menu-toggle" aria-label="Menu">
                {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
              </button>
            </div>
          </div>

          {mobileOpen && (
            <nav className="md:hidden border-t border-borderc py-3 space-y-1" data-testid="mobile-nav">
              <MobileLink to="/dashboard" label="Dashboard" Icon={LayoutDashboard} location={location} close={() => setMobileOpen(false)} />
              {JOURNEY_NAV.map(({ to, label, icon: Icon, step }) => {
                const locked = isLocked(step);
                if (locked) {
                  return (
                    <div key={to} data-testid={`mobile-nav-locked-${step}`}
                      className="flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-slate-400 cursor-not-allowed">
                      <Icon className="h-4 w-4" /> {label}
                      <Lock className="ml-auto h-3.5 w-3.5" />
                    </div>
                  );
                }
                return <MobileLink key={to} to={to} label={label} Icon={Icon} location={location} close={() => setMobileOpen(false)} />;
              })}
              <MobileLink to="/panduan" label="Panduan" Icon={BookOpen} location={location} close={() => setMobileOpen(false)} />
              <MobileLink to="/profil" label="Akun" Icon={User} location={location} close={() => setMobileOpen(false)} />
              {isAdmin && <MobileLink to="/admin" label="Admin" Icon={ShieldCheck} location={location} close={() => setMobileOpen(false)} />}
              <Button variant="ghost" onClick={handleLogout}
                className="w-full justify-start gap-3 px-3 text-red-600 hover:bg-red-50 hover:text-red-600" data-testid="mobile-logout">
                <LogOut className="h-4 w-4" /> Keluar
              </Button>
            </nav>
          )}
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-10">{children}</main>
    </div>
  );
}

function MobileLink({ to, label, Icon, location, close }) {
  const active = location.pathname === to;
  return (
    <Link to={to} onClick={close} data-testid={`mobile-nav-${label.toLowerCase().replace(/ /g, "-")}`}
      className={`flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${
        active ? "bg-brand/10 text-brand" : "text-ink-soft hover:bg-slate-100"}`}>
      <Icon className="h-4 w-4" /> {label}
    </Link>
  );
}

export default function DashboardLayout({ children }) {
  return <LayoutInner>{children}</LayoutInner>;
}
